import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Button, Space, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { useTranslation } from '../../shared/i18n';
import { NAV_SECTIONS } from '../navigation';
import { productIdOf } from '../menuPrefs';

/** Pick which products show in the sidebar and their order (within each section). */
const MenuEditModal = ({ open, shown, onCancel, onSave, onReset, saving }) => {
    const { t } = useTranslation();
    const [order, setOrder] = useState([]);
    const [checked, setChecked] = useState(new Set());

    const products = useMemo(() => {
        const out = [];
        NAV_SECTIONS.forEach((s) => s.items.forEach((it) => {
            const id = productIdOf(it);
            if (id) out.push({ id, section: s.key, label: it.label, icon: it.icon });
        }));
        return out;
    }, []);

    useEffect(() => {
        if (!open) return;
        const ids = products.map((p) => p.id);
        const cur = (shown || ids).filter((id) => ids.includes(id));
        // shown first in their saved order, then the rest in default order
        setOrder([...cur, ...ids.filter((id) => !cur.includes(id))]);
        setChecked(new Set(cur));
    }, [open, shown, products]);

    const byId = Object.fromEntries(products.map((p) => [p.id, p]));
    const move = (id, dir) => {
        const sec = byId[id].section;
        const same = order.filter((x) => byId[x].section === sec);
        const i = same.indexOf(id);
        const j = i + dir;
        if (j < 0 || j >= same.length) return;
        const a = order.indexOf(same[i]);
        const b = order.indexOf(same[j]);
        const next = [...order];
        [next[a], next[b]] = [next[b], next[a]];
        setOrder(next);
    };
    const toggle = (id) => {
        const next = new Set(checked);
        if (next.has(id)) next.delete(id); else next.add(id);
        setChecked(next);
    };

    return (
        <Modal
            open={open}
            title={t('Edit menu')}
            onCancel={onCancel}
            width={420}
            footer={[
                <Button key="reset" onClick={onReset} disabled={saving}>{t('Reset to default')}</Button>,
                <Button key="cancel" onClick={onCancel}>{t('Cancel')}</Button>,
                <Button key="ok" type="primary" loading={saving} onClick={() => onSave(order.filter((id) => checked.has(id)))}>{t('Save')}</Button>,
            ]}
        >
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                {t('Unchecked products stay under More products.')}
            </Typography.Text>
            {NAV_SECTIONS.map((s) => {
                const ids = order.filter((id) => byId[id]?.section === s.key);
                if (!ids.length) return null;
                return (
                    <div key={s.key} style={{ marginBottom: 8 }}>
                        {s.title && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t(s.title)}</Typography.Text>}
                        {ids.map((id, i) => (
                            <div key={id} style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                                <Checkbox checked={checked.has(id)} onChange={() => toggle(id)} style={{ flex: 1 }}>
                                    <Space size={6}>{byId[id].icon}{t(byId[id].label)}</Space>
                                </Checkbox>
                                <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(id, -1)} aria-label={t('Move up')} />
                                <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === ids.length - 1} onClick={() => move(id, 1)} aria-label={t('Move down')} />
                            </div>
                        ))}
                    </div>
                );
            })}
        </Modal>
    );
};

export default MenuEditModal;
